import { t } from '@lingui/core/macro';
import { NoddiInfoBanner } from 'noddi-ui-web';
import type { ReactNode } from 'react';
import { StationaryInvoicePaymentBanners } from './StationaryInvoicePaymentBanners';
import { UpdateAccountPhoneNumberForm } from './UpdateAccountPhoneNumberForm';

type Props = {
  isLoggedIn: boolean;
  hasValidBookingPhone: boolean;
  canUpdateAccountPhone?: boolean;
  isLandline: boolean;
  invalidPhoneDisplay: string;
  /** From useProfileAddressGate — already gated by required. */
  addressSection?: ReactNode;
  organizationName: string;
  registrationNumber?: string | null;
  invoiceToName?: string | null;
  showInvoiceBanner: boolean;
  requiresOrganizationNumber: boolean;
  /** Enables inline add-org-number recovery when the number is missing. */
  organizationId?: number | null;
  onOrganizationNumberSaved?: () => void;
  /** Optional content between address and invoice banners (e.g. org picker). */
  betweenAddressAndInvoice?: ReactNode;
  children?: ReactNode;
};

/**
 * Shared stationary checkout chrome: phone warning, profile address, invoice banners.
 * Used by booking summary, proposal summary, and proposal card.
 */
export const StationaryCheckoutChrome = ({
  isLoggedIn,
  hasValidBookingPhone,
  canUpdateAccountPhone = true,
  isLandline,
  invalidPhoneDisplay,
  addressSection,
  organizationName,
  registrationNumber,
  invoiceToName,
  showInvoiceBanner,
  requiresOrganizationNumber,
  organizationId,
  onOrganizationNumberSaved,
  betweenAddressAndInvoice,
  children
}: Props) => {
  return (
    <>
      {isLoggedIn &&
        !hasValidBookingPhone &&
        (canUpdateAccountPhone ? (
          <UpdateAccountPhoneNumberForm invalidPhoneDisplay={invalidPhoneDisplay} isLandline={isLandline} />
        ) : (
          <NoddiInfoBanner
            variant='warning'
            title={t`Booking contact needs a mobile number`}
            texts={[
              {
                value: t`The contact on this proposal has no supported mobile number. They must update their account, or staff must change the proposal contact before booking.`
              }
            ]}
          />
        ))}

      {addressSection}

      {betweenAddressAndInvoice}

      {isLoggedIn && (
        <StationaryInvoicePaymentBanners
          organizationName={organizationName}
          registrationNumber={registrationNumber}
          invoiceToName={invoiceToName}
          showInvoiceBanner={showInvoiceBanner}
          requiresOrganizationNumber={requiresOrganizationNumber}
          organizationId={organizationId}
          onOrganizationNumberSaved={onOrganizationNumberSaved}
        />
      )}

      {children}
    </>
  );
};
